import { marked } from 'marked';
import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@eluve/components';

// LLM-authored prose tends to use unicode bullets (• ◦ ▪) at line starts in
// place of markdown list markers; CommonMark reads those lines as one soft-
// wrapped paragraph, collapsing the "list" onto a single line. Rewrite them to
// `-` so they parse as real lists.
function normalizeBullets(content: string): string {
  return content.replace(/^([ \t]*)[•◦▪]\s+/gm, '$1- ');
}

// All heading levels collapse to one compact emphasis style — these are card
// prose, not documents, so no outline hierarchy.
function Heading({ children }: { children?: React.ReactNode }) {
  return (
    <p className="mt-2 font-medium text-foreground first:mt-0">{children}</p>
  );
}

// The compact prose component map — the single source of truth shared by both
// the plain `MarkdownProse` and the block-memoized `MemoizedMarkdownProse`. The
// first paragraph renders inline so content can flow after an inline label.
const proseComponents: Components = {
  p: ({ children }) => (
    <p className="mt-1.5 first:mt-0 first:inline">{children}</p>
  ),
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
  ul: ({ children }) => (
    <ul className="mt-1.5 ml-4 list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-1.5 ml-4 list-decimal space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-0.5">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-medium text-foreground">{children}</strong>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline"
    >
      {children}
    </a>
  ),
  code: ({ children }) => (
    <code className="rounded-sm bg-muted px-1 py-0.5 font-mono text-[0.85em]">
      {children}
    </code>
  ),
};

// MarkdownProse — compact markdown for agent-authored plan prose (phase
// descriptions, exit criteria, event responses…). The Plan Builder emits
// markdown inside these free-text fields; render it instead of showing raw
// `**…**`. This inherits the caller's text color/size.
export function MarkdownProse({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={proseComponents}>
        {normalizeBullets(content)}
      </ReactMarkdown>
    </div>
  );
}

// Split markdown into block-level tokens (one raw string per block) so each
// block can be memoized independently — while a message streams, only the last
// block receiving new tokens re-parses; completed blocks stay cached. Mirrors
// the shared `MemoizedMarkdown` (blocks lib) technique, but renders with the
// compact `proseComponents` styling above.
//
// A CommonMark reference-link definition (`[label]: url`) can sit in a different
// block from the `[text][label]` that consumes it; splitting would parse the two
// blocks independently and the link would no longer resolve. Agent plan prose
// rarely uses reference links, but to preserve MarkdownProse's whole-document
// behavior exactly, fall back to a single block whenever a definition is present.
const REFERENCE_DEFINITION = /^[ \t]*\[[^\]]+\]:\s+\S/m;

function splitMarkdownBlocks(markdown: string): string[] {
  if (REFERENCE_DEFINITION.test(markdown)) {
    return [markdown];
  }
  return marked.lexer(markdown).map((token) => token.raw);
}

// A single prose block. Only re-renders when its own raw markdown changes.
const ProseBlock = memo(
  ({ content }: { content: string }) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={proseComponents}>
      {content}
    </ReactMarkdown>
  ),
  (prev, next) => prev.content === next.content,
);

ProseBlock.displayName = 'ProseBlock';

// MemoizedMarkdownProse — block-level-memoized variant of MarkdownProse for the
// streaming chat surfaces (Plan Builder, Care Plan Tailor). Visually identical
// to MarkdownProse: bullets are normalized first, then the content is split
// into blocks rendered inside the SAME single wrapper div, so the `first:inline`
// / `first:mt-0` first-child semantics are preserved.
export function MemoizedMarkdownProse({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const blocks = useMemo(
    () => splitMarkdownBlocks(normalizeBullets(content)),
    [content],
  );
  return (
    <div className={cn('min-w-0', className)}>
      {blocks.map((block, index) => (
        <ProseBlock key={index} content={block} />
      ))}
    </div>
  );
}
